/* MATT'S TWIN — the conversation, inside the product. Both surfaces.
 *
 * IT LOOKS LIKE 33PRIME, NOT LIKE KILROY, AND THAT IS THE POINT. Brandon
 * arrives from the recap page: same dusk, same teal and coral. Blending in
 * would make this a feature of Kilroy Field; standing apart makes it a
 * person's channel. The stylesheet travels with the room payload and is scoped
 * hard under `.mtwin`, so nothing here can touch one element of the app it is a
 * guest inside.
 *
 * THE MARKUP IS A CONTRACT WITH THAT STYLESHEET, NOT A FREE CHOICE. The CSS
 * arrives from MI and names the structure it expects: `.mtwin.on` to show at
 * all, then `.scrim`, `.panel`, `.top`, `.body`, `.msgs`, `.m.you` / `.m.him`.
 * The first version of this file invented its own class names, so `.mtwin`
 * never got `.on` and every inner element was unstyled — clicking the pill
 * unmounted the button and rendered an invisible panel. It read exactly like
 * "the button disappears and nothing opens", because that is what it was.
 * Change a class name here only alongside dock_css.py.
 *
 * ONE COMPONENT, TWO APPS. The only difference between them is `surface`, which
 * prefixes every path before it is looked up or reported. Both apps have a `/`,
 * a `/priorities` and an `/evidence/:id`; without the prefix the twin would
 * narrate the desk's record page while somebody stood in a canyon looking at
 * the phone's.
 *
 * IT OPENS ITSELF ONCE, after a few screens, then collapses. Asking someone
 * what is wrong before they have seen anything is asking them to do our job.
 *
 * NO BADGE, NO COUNT. A red 2 is a notification and a notification creates
 * obligation. The pill says a person is there and waits to be opened.
 *
 * THE TIMING IS DECIDED HERE AND REPORTED NOWHERE. Matt's side hands the
 * records over blind and never learns which screens were seen, so "we never
 * mention what you opened" holds by construction.
 *
 * IT MUST NOT BE ABLE TO COST SOMEBODY A SAVE. On the phone this renders beside
 * an outbox holding the only copy of unsent evidence. Nothing here writes to
 * IndexedDB or OPFS, nothing retries, nothing queues.
 *
 * NO ROOM, NO DOCK. Without a token this renders nothing at all.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  ROOM_COOKIE,
  askTwin,
  fetchRoom,
  readCookie,
  reportVisit,
  screenFor,
  sendAnswer,
  type Ask,
  type Room,
  type Surface,
  type Turn,
} from '@kilroy/shared';

const SCREENS_BEFORE_OPENING = 3;
const SEEN_KEY = 'kilroy.twin.seen';
const OPENED_KEY = 'kilroy.twin.opened';

/* sessionStorage, not local: a demo that remembers yesterday cannot be
   demonstrated twice. Wrapped because Safari private mode throws on access. */
function session(key: string): string {
  try {
    return sessionStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}
function remember(key: string, val: string): void {
  try {
    sessionStorage.setItem(key, val);
  } catch {
    /* a dock that cannot remember is still a dock */
  }
}

export function RoomDock({ surface }: { surface: Surface }) {
  const { pathname } = useLocation();
  const [token, setToken] = useState('');
  const [room, setRoom] = useState<Room | null>(null);
  const [open, setOpen] = useState(false);
  const [answered, setAnswered] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const nudged = useRef(false);

  useEffect(() => {
    const t = readCookie(ROOM_COOKIE);
    if (!t) return;
    setToken(t);
    let live = true;
    void fetchRoom(t).then((r) => {
      if (live) setRoom(r);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const css = room?.css;
    if (!css) return;
    const el = document.createElement('style');
    el.dataset.mtwin = '1';
    el.textContent = css;
    document.head.appendChild(el);
    return () => {
      el.remove();
    };
  }, [room?.css]);

  /* How long they stayed, reported as they leave. */
  useEffect(() => {
    if (!token) return;
    const at = Date.now();
    const send = () => {
      const secs = Math.round((Date.now() - at) / 1000);
      if (secs > 0) reportVisit(token, surface, pathname, secs);
    };
    // Both, because a phone is far more likely to be backgrounded than closed,
    // and unmount alone would miss every one of those.
    const onHide = () => {
      if (document.visibilityState === 'hidden') send();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      send();
    };
  }, [token, surface, pathname]);

  useEffect(() => {
    if (!room || nudged.current || session(OPENED_KEY)) return;
    const seen = new Set(session(SEEN_KEY).split('|').filter(Boolean));
    seen.add(pathname);
    remember(SEEN_KEY, [...seen].join('|'));
    if (seen.size >= SCREENS_BEFORE_OPENING) {
      nudged.current = true;
      remember(OPENED_KEY, '1');
      setOpen(true);
    }
  }, [room, pathname]);

  /* Escape closes it. A panel that covers the product and can only be dismissed
     by finding a small × is a panel people resent. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const note = useMemo(() => screenFor(room, surface, pathname), [room, surface, pathname]);

  const asks = useMemo(() => {
    const all = Array.isArray(room?.asks) ? room.asks : [];
    return all.filter((a) => a?.id && !answered.includes(a.id));
  }, [room, answered]);
  const ask: Ask | undefined = asks[0];

  const answer = useCallback(
    async (text: string, skipped: boolean) => {
      if (!ask || !token) return;
      if (!skipped && !text.trim()) return;
      setAnswered((prev) => [...prev, ask.id]);
      setDraft('');
      await sendAnswer(token, ask.id, text.trim(), skipped);
    },
    [ask, token],
  );

  const send = useCallback(async () => {
    const question = q.trim();
    if (!question || asking) return;
    setQ('');
    const mine: Turn = { role: 'you', text: question };
    setTurns((prev) => [...prev, mine]);
    setAsking(true);
    const reply = await askTwin(token, question, surface, pathname, [...turns, mine]);
    setTurns((prev) => [...prev, reply]);
    setAsking(false);
  }, [q, asking, token, surface, pathname, turns]);

  if (!token || !room) return null;

  return (
    <>
      <button className="mtwinbtn" onClick={() => setOpen(true)} aria-label="Open Matt's twin">
        {/* The sun travels with the payload — our mark, rendered by us, so the
            tenant's app never has to carry a 33prime asset. */}
        <span aria-hidden dangerouslySetInnerHTML={{ __html: room.sun ?? '' }} />
        <span className="lbl">
          Matt&rsquo;s twin
          <span className="sub">{note ? 'what this screen is' : 'ask me anything'}</span>
        </span>
      </button>

      <div className={`mtwin${open ? ' on' : ''}`} role="dialog" aria-label="Matt's twin">
        <div className="scrim" onClick={() => setOpen(false)} />
        <aside className="panel">
          <div className="glow" aria-hidden />
          <div className="top">
            <div className="who">
              <b>Matt&rsquo;s twin</b>
              <span>33prime &middot; trained on this build</span>
            </div>
            <button className="x" onClick={() => setOpen(false)} aria-label="Close">
              &times;
            </button>
          </div>

          <div className="body">
            {note ? (
              <>
                <p className="kk teal">this screen</p>
                <h3>{note.what}</h3>

                {note.goal ? (
                  <>
                    <p className="kk">what it is for</p>
                    <p className="goal">{note.goal}</p>
                  </>
                ) : null}

                {note.ai ? (
                  <>
                    <p className="kk">what it does</p>
                    <p>{note.ai}</p>
                  </>
                ) : null}

                {note.thinking ? (
                  <>
                    <p className="kk">why it is built this way</p>
                    <p>{note.thinking}</p>
                  </>
                ) : null}

                {note.assumed?.length ? (
                  <>
                    <p className="kk">what I assumed</p>
                    <ul>
                      {note.assumed.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </>
            ) : (
              <p className="goal" style={{ marginTop: 22 }}>
                Nothing written up for this screen yet &mdash; but ask me about it and I will tell
                you what I know.
              </p>
            )}

            {ask ? (
              <div className="ask">
                <p className="q">&ldquo;{ask.question}&rdquo;</p>
                {ask.why ? <p>{ask.why}</p> : null}
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="However you would say it."
                  rows={3}
                />
                <div className="row">
                  <button
                    className="send"
                    disabled={!draft.trim()}
                    onClick={() => void answer(draft, false)}
                  >
                    Send
                  </button>
                  <button className="skip" onClick={() => void answer('', true)}>
                    Not now
                  </button>
                </div>
              </div>
            ) : (
              <p className="done" style={{ marginTop: 26 }}>
                That is everything I wanted to ask. Anything you tell me below still reaches Matt.
              </p>
            )}

            <div className="chat">
              <p className="kk teal">ask me about this screen</p>
              {turns.length ? (
                <div className="msgs">
                  {turns.map((t, i) => (
                    <div key={i} className={t.role === 'you' ? 'm you' : 'm him'}>
                      {t.role === 'twin' ? <span className="nm">Matt&rsquo;s twin</span> : null}
                      {t.text}
                    </div>
                  ))}
                  {asking ? <div className="m him think">reading the record&hellip;</div> : null}
                </div>
              ) : null}
              <textarea
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
                placeholder="Why is this here? What is real? Tell me it is wrong."
                rows={2}
                aria-label="Ask a question"
              />
              <div className="row">
                <button className="send" disabled={!q.trim() || asking} onClick={() => void send()}>
                  Ask
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
